/**
 * MehtaPartheniadesPage — Mehta-Partheniades cohesive sediment erosion/deposition
 *
 *   E = M · (τ_b / τ_ce − 1)        for τ_b > τ_ce                (erosion rate, kg/m²/s)
 *   D = w_s · C · (1 − τ_b / τ_cd)  for τ_b < τ_cd                (deposition rate, kg/m²/s)
 *
 * Bed shear from near-bed velocity (Soulsby quadratic drag):
 *   τ_b = ρ · C_d · u_b²     C_d ≈ 0.0025 over smooth cohesive bed
 *
 * Reference: Partheniades 1965; Krone 1962; Mehta 1986 (Estuarine Cohesive Sediment Dynamics, Springer).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Layers, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function MehtaPartheniadesPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('mehta_partheniades');
  const [u_b, setUb] = useState(0.04);          // m/s near-bed velocity (from plume induction)
  const [tau_ce, setTauCe] = useState(0.15);    // Pa critical erosion shear
  const [tau_cd, setTauCd] = useState(0.06);    // Pa critical deposition shear
  const [M, setM] = useState(0.00005);          // kg/m²/s erosion constant
  const [w_s, setWs] = useState(2e-5);          // m/s floc settling velocity
  const [C_susp, setC] = useState(15);          // mg/L suspended cohesive sediment

  const R = useMemo(() => {
    const rho = 1000;
    const C_d = 0.0025;
    const tau_b = rho * C_d * u_b * u_b;         // Pa
    const erosion = tau_b > tau_ce ? M * (tau_b / tau_ce - 1) : 0;  // kg/m²/s
    const deposition = tau_b < tau_cd ? w_s * (C_susp / 1000) * (1 - tau_b / tau_cd) : 0;  // kg/m²/s (C in kg/m³)
    const netFlux = erosion - deposition;  // positive = bed losing mass (eroding)
    const regime = tau_b > tau_ce ? 'erosion' : tau_b < tau_cd ? 'deposition' : 'stable';
    // Convert to annual flux (kg/m²/yr)
    const annual = netFlux * 86400 * 365;
    return { tau_b, erosion, deposition, netFlux, regime, annual };
  }, [u_b, tau_ce, tau_cd, M, w_s, C_susp]);

  const fmt = (n: number, d = 4) => isFinite(n) ? n.toExponential(d) : '—';
  const fmt2 = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';

  const regCls =
    R.regime === 'erosion'
      ? { border: 'border-red-700', bg: 'bg-red-900/15', label: 'text-red-300', val: 'text-red-200' }
      : R.regime === 'deposition'
        ? { border: 'border-emerald-700', bg: 'bg-emerald-900/15', label: 'text-emerald-300', val: 'text-emerald-200' }
        : { border: 'border-sky-700', bg: 'bg-sky-900/15', label: 'text-sky-300', val: 'text-sky-200' };

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Layers} title="Module — Mehta-Partheniades Cohesive Sediment"
        subtitle="Floc erosion / deposition kinetics under aeration-induced bed shear"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('mehta_partheniades')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="mehta_partheniades" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Bed shear τ_b</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt2(R.tau_b, 3)} Pa</div>
          <div className="text-xs text-slate-500 mt-1">u_b = {u_b.toFixed(3)} m/s</div>
        </div>
        <div className={`rounded-xl border-2 ${regCls.border} ${regCls.bg} p-4`}>
          <div className={`text-xs uppercase tracking-wider ${regCls.label}`}>Regime</div>
          <div className={`text-lg font-bold ${regCls.val} mt-1 uppercase`}>{R.regime}</div>
          <div className="text-xs text-slate-500 mt-1">τ_ce = {tau_ce} • τ_cd = {tau_cd}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Erosion E</div>
          <div className="text-xl font-bold text-slate-100 mt-1">{fmt(R.erosion)}</div>
          <div className="text-xs text-slate-500 mt-1">kg/m²/s</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Deposition D</div>
          <div className="text-xl font-bold text-slate-100 mt-1">{fmt(R.deposition)}</div>
          <div className="text-xs text-slate-500 mt-1">kg/m²/s</div>
        </div>
      </div>

      <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4">
        <div className="text-xs uppercase tracking-wider text-amber-300">Net annual mass flux</div>
        <div className="text-2xl font-bold text-amber-200 mt-1">{fmt2(R.annual, 1)} kg/m²/yr</div>
        <div className="text-xs text-slate-500 mt-1">{R.annual > 0 ? 'bed losing mass — sediment uplifted into water column' : 'bed gaining mass — net deposition'}</div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-6 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">u_b: <strong className="text-slate-100">{u_b.toFixed(3)} m/s</strong></label>
          <input type="range" min={0} max={0.20} step={0.002} value={u_b} onChange={e => setUb(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">τ_ce: <strong className="text-slate-100">{tau_ce.toFixed(2)} Pa</strong></label>
          <input type="range" min={0.02} max={0.80} step={0.01} value={tau_ce} onChange={e => setTauCe(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">τ_cd: <strong className="text-slate-100">{tau_cd.toFixed(2)} Pa</strong></label>
          <input type="range" min={0.01} max={0.30} step={0.005} value={tau_cd} onChange={e => setTauCd(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">M: <strong className="text-slate-100">{(M * 1e5).toFixed(2)} ×10⁻⁵</strong></label>
          <input type="range" min={0.000005} max={0.0005} step={0.000005} value={M} onChange={e => setM(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">w_s: <strong className="text-slate-100">{(w_s * 1e6).toFixed(0)} µm/s</strong></label>
          <input type="range" min={1e-6} max={1e-4} step={1e-6} value={w_s} onChange={e => setWs(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">C suspended: <strong className="text-slate-100">{C_susp} mg/L</strong></label>
          <input type="range" min={1} max={120} step={1} value={C_susp} onChange={e => setC(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> How this replaces the blanket "resuspension penalty"</div>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Aeration plumes induce a near-bed return flow u_b. We compute u_b from the plume coverage and bed depth.</li>
          <li>u_b is converted to bed shear τ_b via Soulsby quadratic drag (C_d = 0.0025 for smooth cohesive bed).</li>
          <li>If τ_b &gt; τ_ce the bed erodes at rate M·(τ_b/τ_ce − 1). Cohesive beds typically have τ_ce = 0.1-0.3 Pa.</li>
          <li>If τ_b &lt; τ_cd flocs settle and the bed accumulates. τ_cd is always &lt; τ_ce (hysteresis).</li>
          <li>Between τ_cd and τ_ce: stable — neither erodes nor deposits.</li>
        </ol>
        <p className="mt-2">For typical Barcombe aeration u_b ≈ 0.03-0.05 m/s, giving τ_b ≈ 0.002-0.006 Pa — well below τ_ce. The cohesive bed
        therefore remains stable; what the Bryant 2011 resuspension penalty flags as "risk" is in fact deposition (D-regime). This module gives the
        physically correct verdict.</p>
        <p className="italic text-slate-500 mt-2">Reference: Partheniades (1965) J. Hydraul. Div. ASCE 91: 105-139; Mehta (1986) Estuarine Cohesive Sediment Dynamics, Springer.</p>
      </div>
    </div>
  );
}
